import { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { createCopyRequest, newIdempotencyKey } from '../../api/originatorApi';

// Copy request is the only custody request exposed in the UI.
// Pledge, release, and transfer creators stay in originatorApi.
export const REQUEST_TYPES = [
  { value: 'COPY_REQUEST', label: 'Copy request', submit: createCopyRequest },
];

const inputClass =
  'w-full rounded-evault border border-evault-select-ring/70 bg-white px-3 py-2 text-sm text-umb-neutral-dark shadow-sm focus:border-evault-teal focus:outline-none focus:ring-2 focus:ring-evault-teal/20';

/**
 * Create a copy request. The idempotency key
 * is generated once per submit intent (when the modal opens) and reused across
 * retries/double-clicks so the backend can dedupe.
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSubmitted?: (response: object) => void,
 *   assetId?: string,
 *   lockAsset?: boolean,
 *   defaultType?: string,
 * }} props
 */
export function CreateRequestModal({
  open,
  onClose,
  onSubmitted,
  assetId: presetAssetId,
  lockAsset = false,
  defaultType = 'COPY_REQUEST',
}) {
  const [assetId, setAssetId] = useState(presetAssetId || '');
  const [requestType, setRequestType] = useState(defaultType);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Reset form state and mint a fresh idempotency key each time the modal opens.
  useEffect(() => {
    if (open) {
      setAssetId(presetAssetId || '');
      setRequestType(defaultType);
      setNotes('');
      setReason('');
      setError('');
      setSuccess('');
      setIdempotencyKey(newIdempotencyKey());
    }
  }, [open, presetAssetId, defaultType]);

  if (!open) return null;

  const selected = REQUEST_TYPES.find((type) => type.value === requestType) ?? REQUEST_TYPES[0];

  async function handleSubmit(event) {
    event.preventDefault();
    if (!assetId.trim()) {
      setError('Asset ID is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const notesValue =
        requestType === 'COPY_REQUEST' ? reason.trim() || null : notes.trim() || null;
      const response = await selected.submit(
        assetId.trim(),
        { notes: notesValue },
        idempotencyKey
      );
      setSuccess('Request submitted.');
      if (onSubmitted) onSubmitted(response);
    } catch (requestError) {
      setError(requestError.message || 'Unable to submit request.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-request-heading"
    >
      <div className="w-full max-w-md rounded-evault border border-evault-border bg-white p-5 shadow-xl sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <h2 id="create-request-heading" className="text-base font-semibold text-umb-neutral-dark">
            New custody request
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-2 py-1 text-sm text-umb-neutral-dark/60 hover:bg-evault-surface"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <p className="mt-1 text-xs text-evault-muted">
          Creates a workflow request routed to the custodian. It does not execute the action.
        </p>

        {success ? (
          <div className="mt-5 space-y-4">
            <p className="rounded-evault border border-umb-success/20 bg-umb-success/[0.06] px-3 py-3 text-sm font-medium text-umb-success" role="status">
              {success}
            </p>
            <div className="flex justify-end">
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form className="mt-5 space-y-4" onSubmit={handleSubmit} noValidate>
            <label className="block">
              <span className="text-xs font-medium text-umb-neutral-dark/70">Asset ID</span>
              <input
                className={`${inputClass} mt-1 ${lockAsset ? 'bg-evault-surface/60' : ''}`}
                value={assetId}
                onChange={(event) => setAssetId(event.target.value)}
                placeholder="Asset UUID"
                readOnly={lockAsset}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-umb-neutral-dark/70">Request type</span>
              <select
                className={`${inputClass} mt-1`}
                value={requestType}
                onChange={(event) => setRequestType(event.target.value)}
              >
                {REQUEST_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>
            {requestType === 'COPY_REQUEST' ? (
              <label className="block">
                <span className="text-xs font-medium text-umb-neutral-dark/70">Reason</span>
                <textarea
                  className={`${inputClass} mt-1`}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Reason for this copy request"
                />
              </label>
            ) : (
              <label className="block">
                <span className="text-xs font-medium text-umb-neutral-dark/70">Notes (optional)</span>
                <textarea
                  className={`${inputClass} mt-1`}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Add context for the custodian…"
                />
              </label>
            )}

            {error ? (
              <p className="text-sm text-umb-error" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit request'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
